import { getUserProfile, onSetAccount } from 'app/actions/authActions';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Button from 'shared/components/Button';
import Input from 'shared/components/Input';
import RadioBox from 'shared/components/RadioBox';
import Select from 'shared/components/Select';
import { getDays, getError, getMonths, getYears } from 'utils/appHelpers';
import { PenIconFilled } from 'shared/components/Icons';
import AvatarSelectionModal from './components/AvatarSelectionModal';
import { API, graphqlOperation } from 'aws-amplify';
import { updateProfileMutation } from 'graphql/user/mutations';
import { useAlert } from 'react-alert';
import AvatarImg from 'shared/components/AvatarImg';

const getBirthday = form => {
  const { day, month, year } = form;
  if (!day || !month || !year) return null;
  return new Date(`${month}/${day}/${year}`);
};

const setInitialBirthday = birthday => {
  if (!birthday) return { month: '', day: '', year: '' };
  const date = new Date(birthday);
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  return { month, day, year };
};

const ProfileEdit = () => {
  const alert = useAlert();
  const dispatch = useDispatch();
  const account = useSelector(state => state.account);
  const [avatarModalState, setAvatarModalState] = useState(false);
  const [file, setFile] = useState();
  const { t } = useTranslation();
  const [error, setError] = useState();
  const [fetch, setFetch] = useState(false);
  const [form, setForm] = useState({
    name: account.name,
    phone: '',
    email: account.email,
    gender: account.gender,
    ...setInitialBirthday(account.birthday),
  });

  const onFormChange = ({ target }) => {
    const { name, value } = target;
    setForm({ ...form, [name]: value });
  };

  const onSelectGender = ({ target }) => {
    setForm({ ...form, gender: target.name });
  };

  const onFormSubmit = async e => {
    setFetch(true);
    setError();
    e.preventDefault();
    try {
      const birthday = getBirthday(form);
      const profile = { birthday, email: form.email, name: form.name, gender: form.gender };
      const res = await API.graphql(graphqlOperation(updateProfileMutation, { profile }));
      if (res.data.updateProfile.success) {
        dispatch(onSetAccount({ ...account, ...profile }));
        alert.success(t('msg_success_update_profile'));
      } else {
        setError(getError(res.data.updateProfile.error));
      }
    } catch (err) {
      setError(getError(err));
    } finally {
      setFetch(false);
    }
  };

  const onProfileImageSuccess = async () => {
    const user = await getUserProfile();
    dispatch(onSetAccount({ ...user }));
  };

  const onSelectAvatarFile = e => {
    setFile(e.target.files[0]);
    setAvatarModalState(true);
  };

  return (
    <div>
      <div className='col-12 col-md-6 p-0 m-auto'>
        <h1>{t('title_edit_profile')}</h1>
        <p>{t('sub_title_edit_profile')}</p>
        <div className='profile-avatar-edit my-15 text-center'>
          <label htmlFor='avatar' className='btn p-0 rounded-100p d-flex align-items-center justify-content-center'>
            <PenIconFilled width={20} height={20} />
          </label>
          <input
            type='file'
            accept='image/png, image/jpeg'
            name='avatar'
            id='avatar'
            className='d-none'
            key={avatarModalState}
            onChange={onSelectAvatarFile}
          />
          <AvatarImg
            name={account.name}
            picture={account.picture}
            className='border'
            showNoImageIcon
            width='90px'
            height='90px'
          />
        </div>
        <form onSubmit={onFormSubmit}>
          <div className='row'>
            <div className='col-12'>
              <Input
                type='text'
                name='name'
                onChange={onFormChange}
                value={form.name}
                label={t('label_fname')}
                placeholder=''
                required
              />
            </div>
          </div>
          <div className='row'>
            <div className='col-12 col-md-12'>
              <Input
                type='email'
                name='email'
                onChange={onFormChange}
                value={form.email}
                label={t('label_email')}
                placeholder=''
                required
              />
            </div>
            {/* <div className='col-12 col-md-6'>
              <Input
                type='text'
                name='number'
                onChange={onFormChange}
                value={form.phone}
                label={t('label_number')}
                placeholder=''
              />
            </div> */}
          </div>
          <div className='form-block mb-8 mb-md-10'>
            <label className='fw-700 mb-5 mb-md-6'>{t('label_birthday')}</label>
            <div className='d-flex row flex-wrap justify-content-between'>
              <div className='mb-md-0 col-4 col-md-4 pr-md-4 pr-2'>
                <Select
                  name='month'
                  placeholder={t('ph_month')}
                  value={form.month}
                  items={getMonths()}
                  onChange={onFormChange}
                  className='mb-0 pr-3 border rounded-7'
                  selectClassName={'border-0'}
                />
              </div>
              <div className='mb-md-0 col-4 col-md-4 px-md-4 pl-2 pr-4'>
                <Select
                  name='day'
                  placeholder={t('ph_day')}
                  value={form.day}
                  items={getDays(form.day)}
                  onChange={onFormChange}
                  className='mb-0 pr-3 border rounded-7'
                  selectClassName={'border-0'}
                />
              </div>
              <div className='col-4 col-md-4 pl-0 pl-md-4 pr-8 pr-md-8'>
                <Select
                  name='year'
                  placeholder={t('ph_year')}
                  value={form.year}
                  items={getYears()}
                  onChange={onFormChange}
                  className='mb-0 pr-3 border rounded-7'
                  selectClassName={'border-0'}
                />
              </div>
            </div>
          </div>
          <div className='form-block mb-8 mb-md-10'>
            <label className='fw-700 mb-5 mb-md-6'>{t('label_gender')}</label>
            <div className='d-flex row flex-wrap justify-content-between'>
              <div className='mb-6 mb-md-0 col-6 col-md-4 pr-md-4 pr-2'>
                <RadioBox
                  checked={form.gender === 'FEMALE'}
                  id='radio1'
                  name='FEMALE'
                  onChange={onSelectGender}
                  className='custom-radio-reverse with-border py-0 pr-23 pl-4'
                  label={t('ph_female')}
                />
              </div>
              <div className='mb-6 mb-md-0 col-6 col-md-4 px-md-4 pl-2'>
                <RadioBox
                  checked={form.gender === 'MALE'}
                  id='radio2'
                  name='MALE'
                  onChange={onSelectGender}
                  className='custom-radio-reverse with-border py-0 pr-23 pl-4'
                  label={t('ph_male')}
                />
              </div>
              <div className='col-6 col-md-4 pl-md-4 pr-2 pr-md-8'>
                <RadioBox
                  checked={form.gender === 'OTHER'}
                  id='radio3'
                  name='OTHER'
                  onChange={onSelectGender}
                  className='custom-radio-reverse with-border py-0 pr-23 pl-4'
                  label={t('ph_custom')}
                />
              </div>
            </div>
          </div>
          {error && <div className='invalid-feedback d-block mt-5'>{error}</div>}
          <div className='my-12 my-md-16'>
            <Button type='submit' disabled={fetch} className='btn-primary'>
              {t('btn_save')}
            </Button>
          </div>
        </form>
      </div>
      {avatarModalState && file && (
        <AvatarSelectionModal
          file={file}
          onClose={() => setAvatarModalState(false)}
          onSuccess={onProfileImageSuccess}
        />
      )}
    </div>
  );
};

export default ProfileEdit;
